import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { translateBackendErrorCode } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { Button, Icon, Loading, replaceStringPlaceholder, Typography, useTranslations } from "@jti/ui";
import ArrowFull from "@jti/ui/icons/arrow-full.svg";
import { ImageBackgroundContainer } from "../image-background-container";
import { PrizeSpinner } from "../prize-spinner";
import styles from "./QrCodeLottery.module.scss";
const ActionButtons = ({ ctaLabel, ctaUrl, secondaryCtaLabel, secondaryCtaUrl, onClick, disabled, }) => {
    if (!secondaryCtaLabel || !secondaryCtaUrl) {
        return (_jsx(Button, { size: "medium", color: "primary", theme: "dark", onClick: onClick, disabled: disabled, children: ctaLabel }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Button, { size: "medium", theme: "dark", color: "secondary", href: ctaUrl, disabled: disabled, children: ctaLabel }), _jsx(Button, { size: "medium", theme: "dark", color: "primary", href: secondaryCtaUrl, disabled: disabled, children: secondaryCtaLabel })] }));
};
export const QrCodeLottery = ({ backgroundImage, mobileBackgroundImage, generalTitle, won, alreadyParticipated, lost, 
// logged, // to be used in DESP-63972
notLogged, spin, ctasUnhappyPath, wheelPointsLabel, wheelLoseLabel, wheelTitle, }) => {
    const isLogged = useSelector((state) => state.loyaltyCore.isLogged);
    const [{ earnedPoints, drawnIndex }, setOption] = useState({
        earnedPoints: 0,
        drawnIndex: undefined,
    });
    const { __ } = useTranslations();
    const [isLoading, setIsLoading] = useState(false);
    const [isHandleQrCodeCheckPending, setIsHandleQrCodeCheckPending] = useState(false);
    const [lotteryOptions, setLotteryOptions] = useState([]);
    const [lotteryState, setLotteryState] = useState(isLogged ? "spin" : "notLogged");
    const shouldDisableButton = isLoading || (lotteryState === "spin" && drawnIndex !== undefined);
    const qrCodeLotteryClasses = classNames(styles.wrapper, {
        [styles.fullCover]: lotteryState === "alreadyParticipated" || lotteryState === "lost",
        [styles.minHeight]: lotteryState === "logged" || lotteryState === "notLogged",
        [styles.centeredLoading]: isHandleQrCodeCheckPending,
    });
    const { ciam_token } = getUser();
    const ctasUnluckyPathProps = {
        ctaLabel: ctasUnhappyPath.ctaLabel,
        ctaUrl: ctasUnhappyPath.ctaUrl,
        secondaryCtaLabel: ctasUnhappyPath.secondaryCtaLabel,
        secondaryCtaUrl: ctasUnhappyPath.secondaryCtaUrl,
    };
    const lotteryOptionsLabels = useMemo(() => lotteryOptions.map(({ points, is_win }) => {
        return is_win
            ? replaceStringPlaceholder(wheelPointsLabel, [{ placeholder: "points", value: points }], false)
            : wheelLoseLabel;
    }), [lotteryOptions, wheelLoseLabel, wheelPointsLabel]);
    const handleQrLotteryOutcome = useCallback(async () => {
        const params = new URLSearchParams(window.location.search);
        const qrCodeId = params.get("id");
        if (ciam_token && qrCodeId) {
            setIsLoading(true);
            try {
                const { is_rewarded, earned_points, selected_outcome } = await LoyaltyApi.init().scanQrAction(qrCodeId, ciam_token);
                const optionIndex = lotteryOptions.findIndex(({ name }) => name === selected_outcome.name);
                if (!selected_outcome.is_win) {
                    setOption({ earnedPoints: 0, drawnIndex: optionIndex });
                }
                else if (is_rewarded) {
                    setOption({ earnedPoints: earned_points, drawnIndex: optionIndex });
                }
                setTimeout(() => setLotteryState(selected_outcome.is_win ? "won" : "lost"), 2000);
            }
            catch (error) {
                const preparedError = translateBackendErrorCode(error.error_code, __("loyalty.qrCodePointsCollection.error"));
                console.error(error);
                toast.error(preparedError);
            }
            finally {
                setIsLoading(false);
            }
        }
    }, [__, ciam_token, lotteryOptions]);
    const buttonsConfig = {
        // DESP-63972 logged status
        // logged: {
        //   ctaLabel: logged.ctaLabel,
        //   onClick: () => {},
        //   disabled: false,
        // },
        notLogged: {
            ctaLabel: notLogged.ctaLabel,
            ctaUrl: notLogged.ctaUrl,
            secondaryCtaLabel: notLogged.secondaryCtaLabel,
            secondaryCtaUrl: notLogged.secondaryCtaUrl,
        },
        spin: {
            ctaLabel: spin.ctaLabel,
            onClick: handleQrLotteryOutcome,
            disabled: shouldDisableButton || !lotteryOptions.length,
        },
        won: {
            ctaLabel: won.ctaLabel,
            ctaUrl: won.ctaUrl,
            secondaryCtaLabel: won.secondaryCtaLabel,
            secondaryCtaUrl: won.secondaryCtaUrl,
        },
        lost: ctasUnluckyPathProps,
        alreadyParticipated: ctasUnluckyPathProps,
    };
    const title = {
        // logged: logged.title, // DESP-63972 logged status
        notLogged: notLogged.title,
        spin: spin.title,
        won: replaceStringPlaceholder(won.title, [{ placeholder: "points", value: earnedPoints }], false),
        lost: lost.title,
        alreadyParticipated: alreadyParticipated.title,
    };
    const description = {
        // logged: logged.description, // DESP-63972 logged status
        notLogged: notLogged.description,
        spin: spin.description,
        won: won.description,
        lost: lost.description,
        alreadyParticipated: alreadyParticipated.description,
    };
    const showPrizeWheel = lotteryState === "spin" || lotteryState === "won" || lotteryState === "lost";
    // using this useEffect is temporary solution before DESP-63972 is implemented
    useEffect(() => {
        const handleQrCodeCheck = async () => {
            const params = new URLSearchParams(window.location.search);
            const qrCodeId = params.get("id");
            const onError = (error = null) => {
                const preparedError = translateBackendErrorCode(error?.error_code, __("loyalty.qrCodePointsCollection.error"));
                toast.error(preparedError);
            };
            if (ciam_token && qrCodeId) {
                setIsHandleQrCodeCheckPending(true);
                try {
                    const { is_lottery_triggered, lottery_outcomes } = await LoyaltyApi.init().getQrCodeDetails({
                        ciam_token,
                        qrCodeId,
                    });
                    if (is_lottery_triggered) {
                        setLotteryState("alreadyParticipated");
                    }
                    else {
                        setLotteryOptions(lottery_outcomes);
                    }
                }
                catch (error) {
                    console.error(error);
                    setLotteryState("alreadyParticipated");
                    onError(error);
                }
                finally {
                    setIsHandleQrCodeCheckPending(false);
                }
            }
            else if (!qrCodeId) {
                onError();
            }
        };
        if (lotteryState === "spin") {
            handleQrCodeCheck();
        }
        return () => null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotteryState]);
    return (_jsxs("div", { className: styles.qrCodeLottery, children: [_jsx(Typography, { variant: "body2", weight: "medium", className: styles.generalTitle, children: generalTitle }), _jsx(ImageBackgroundContainer, { background: backgroundImage, mobileBackground: mobileBackgroundImage, fullWidth: false, withBlur: lotteryState !== "logged" && lotteryState !== "notLogged", className: styles.imageContainer, children: _jsx("div", { className: qrCodeLotteryClasses, children: isHandleQrCodeCheckPending ? (_jsx(Loading, { whiteDots: true })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.content, children: [_jsx(Typography, { className: styles.textColor, variant: "h2", weight: "light", children: title[lotteryState] }), _jsx(Typography, { className: classNames(styles.description, styles.textColor), variant: "body2", weight: "light", children: description[lotteryState] }), _jsx("div", { className: styles.buttonsWrapper, children: _jsx(ActionButtons, { ...buttonsConfig[lotteryState] }) })] }), showPrizeWheel && (_jsxs("div", { className: classNames(styles.wheelContainer, {
                                    [styles.titlePosition]: lotteryState === "spin",
                                }), children: [_jsx(PrizeSpinner, { options: lotteryOptionsLabels, drawnIndex: drawnIndex, isLoading: isLoading, className: styles.wheelPosition, title: wheelTitle }), _jsx(Icon, { className: styles.icon, component: ArrowFull, width: "55px", height: "91px" })] }))] })) }) })] }));
};
//# sourceMappingURL=QrCodeLottery.js.map